import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useAuth } from "@/lib/auth";
import { PhoneIcon } from "@heroicons/react/solid";
import { FaInstagram, FaFacebookF, FaTwitter, FaLinkedinIn } from "react-icons/fa";
import PurpleDoorLogoHorizontal from "../Logo/PurpleDoorLogoHorizontal";
import usePurpleDoorAccess from "./usePurpleDoorAccess";

const navItems = [
    { label: "Search", href: "/purpledoor/search" },
    { label: "Favorites", href: "/purpledoor/favorites" },
];

// Purple "talk to a counselor" banner, mirrors the main site (HomeHeader).
const CounselorBanner = () => (
    <div className="w-full bg-primary-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-10 sm:h-12 flex items-center justify-center gap-3">
            <span className="hidden sm:block text-sm sm:text-lg font-bold font-headline">
                Talk to an Assisted Living Counselor
            </span>
            <span className="block sm:hidden text-sm font-bold font-headline">Call Us</span>
            <a
                href="tel:6127877533"
                className="flex items-center gap-2 bg-gray-100 hover:bg-white rounded-full px-3 py-1 transition-colors"
            >
                <span className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                    <PhoneIcon className="w-3 h-3 text-white" />
                </span>
                <span className="text-xs sm:text-base font-bold text-gray-700">
                    612-787-7533
                </span>
            </a>
        </div>
    </div>
);

const Header = () => {
    const router = useRouter();
    const auth = useAuth();
    const isActive = (href) => router.pathname === href;

    return (
        <header className="sticky top-0 z-30 bg-gray-50 border-b-2 border-gray-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
                <Link href="/purpledoor" className="flex items-center gap-2 group">
                    <span className="scale-[0.55] sm:scale-75 origin-left -my-2">
                        <PurpleDoorLogoHorizontal />
                    </span>
                    <span className="hidden sm:inline text-xs font-bold text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-full">
                        AI Search
                    </span>
                </Link>

                <nav className="flex items-center gap-1">
                    {navItems.map((item) => (
                        <Link
                            key={item.href}
                            href={item.href}
                            className={`px-3 py-1.5 text-sm font-bold rounded transition-colors ${
                                isActive(item.href)
                                    ? "text-primary-600 bg-primary-50"
                                    : "text-primary-500 hover:bg-gray-200"
                            }`}
                        >
                            {item.label}
                        </Link>
                    ))}

                    {auth?.user ? (
                        <button
                            onClick={() => auth.signOut("/purpledoor")}
                            className="ml-1 px-3 py-1.5 text-sm font-bold text-gray-500 hover:text-red-500 rounded hover:bg-gray-200 transition-colors"
                        >
                            Sign Out
                        </button>
                    ) : null}
                </nav>
            </div>
        </header>
    );
};

const SOCIALS = [
    { href: "https://instagram.com/purpledoorfinders", Icon: FaInstagram },
    { href: "https://facebook.com/purpledoorfinders", Icon: FaFacebookF },
    { href: "https://twitter.com/findpurpledoor", Icon: FaTwitter },
    { href: "https://linkedin.com/company/purple-door-finders", Icon: FaLinkedinIn },
];

// Dark footer styled like the main site (HomeFooter), slimmed for this section.
const Footer = () => (
    <footer style={{ backgroundColor: "#302d33" }} className="text-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-3">
                <span className="scale-[0.6] origin-left -my-3">
                    <PurpleDoorLogoHorizontal inverse />
                </span>
            </div>

            <div className="flex items-center gap-3">
                {SOCIALS.map(({ href, Icon }) => (
                    <Link
                        key={href}
                        href={href}
                        className="p-2 bg-gray-600 rounded-full hover:bg-gray-500 transition-colors"
                    >
                        <Icon className="w-4 h-4 text-white" />
                    </Link>
                ))}
            </div>

            <div className="flex flex-col sm:items-end gap-1 text-sm">
                <div className="flex gap-4">
                    <Link href="/pages/privacy-policy" className="hover:text-primary-200">
                        Privacy
                    </Link>
                    <Link href="/pages/terms-of-use" className="hover:text-primary-200">
                        Terms
                    </Link>
                    <Link href="/contact-us" className="hover:text-primary-200">
                        Contact
                    </Link>
                </div>
                <span className="text-gray-400">
                    &copy; {new Date().getFullYear()} Purple Door, LLC
                </span>
            </div>
        </div>
    </footer>
);

/**
 * Layout for the PurpleDoor section.
 * - variant="site" (default): full chrome (counselor banner + header + dark footer)
 *   for content pages like the landing and favorites.
 * - variant="app": compact header only (no banner/footer) for the two-pane
 *   search view, which manages its own full-height layout.
 */
const PurpleDoorLayout = ({ children, variant = "site" }) => {
    const isApp = variant === "app";
    const access = usePurpleDoorAccess();

    // Hide the entire section (chrome included) until the localStorage
    // access key is confirmed; the hook redirects to "/" when it is missing.
    if (access !== "granted") return null;

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 font-body">
            {!isApp ? <CounselorBanner /> : null}
            <Header />
            <main className="flex-1">{children}</main>
            {!isApp ? <Footer /> : null}
        </div>
    );
};

export default PurpleDoorLayout;
